import { supabase } from "@/integrations/supabase/client";
import { prepareUploadFile, type UploadFileInfo } from "@/lib/uploadFormat";

export class PortalRequestError extends Error {
  /** Set on upload failures so a 415 or 413 says which format was sent. */
  fileInfo?: UploadFileInfo;

  constructor(
    public readonly kind: "session" | "network" | "timeout" | "http" | "response",
    public readonly endpoint: string,
    public readonly status?: number,
    public readonly requestId?: string,
    public readonly phase?: "request" | "body",
  ) {
    super(`Portal request failed (${kind}${status ? ` ${status}` : ""})`);
    this.name = "PortalRequestError";
  }
}

// Never include private object keys, filenames or query strings in diagnostics.
function endpointLabel(path: string) {
  return path.startsWith("/api/files/") ? "/api/files/:key"
    : path.startsWith("/api/forms/") ? "/api/forms/:template"
    : path.split("?")[0];
}

async function bounded<T>(promise: PromiseLike<T>, timeoutMs: number, endpoint: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new PortalRequestError("timeout", endpoint)), timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer!);
  }
}

let refreshInFlight: ReturnType<typeof supabase.auth.refreshSession> | undefined;
async function accessToken(refresh: boolean, endpoint: string) {
  if (refresh && !refreshInFlight) {
    refreshInFlight = supabase.auth.refreshSession().finally(() => { refreshInFlight = undefined; });
  }
  const { data, error } = await bounded(
    refresh ? refreshInFlight! : supabase.auth.getSession(), 20_000, endpoint,
  );
  if (error) {
    const network = error.name === "AuthRetryableFetchError" || error instanceof TypeError;
    throw new PortalRequestError(network ? "network" : "session", endpoint);
  }
  if (!data.session?.access_token) throw new PortalRequestError("session", endpoint, 401);
  return data.session.access_token;
}

type Options = Omit<RequestInit, "signal"> & { authenticated?: boolean; timeoutMs?: number };

/** Retry reads once, and renew an expired session once. Never replay ambiguous writes. */
export async function portalRequest<T>(
  path: string,
  options: Options,
  read: (response: Response) => Promise<T>,
): Promise<T> {
  const { authenticated = true, timeoutMs = 60_000, ...init } = options;
  const endpoint = endpointLabel(path);
  // Authorization must only ever be sent to our own API.
  if (!path.startsWith("/api/")) throw new Error("Portal requests require a same-origin API path");
  let token = authenticated ? await accessToken(false, endpoint) : undefined;
  let refreshed = false;
  let retriedRead = false;
  const isRead = !init.method || init.method.toUpperCase() === "GET";
  for (;;) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const headers = new Headers(init.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    let responseStatus: number | undefined;
    let requestId: string | undefined;
    let phase: "request" | "body" = "request";
    try {
      const response = await fetch(path, { ...init, headers, signal: controller.signal });
      responseStatus = response.status;
      requestId = response.headers.get("X-Request-ID") || response.headers.get("CF-Ray") || undefined;
      if (response.status === 401 && authenticated && !refreshed) {
        refreshed = true;
        await response.body?.cancel();
        clearTimeout(timer);
        token = await accessToken(true, endpoint);
        continue;
      }
      if (!response.ok) {
        await response.body?.cancel();
        throw new PortalRequestError(response.status === 401 ? "session" : "http", endpoint, response.status, requestId);
      }
      // Keep the timeout active while reading the response body too.
      phase = "body";
      return await read(response);
    } catch (error) {
      const failure = error instanceof PortalRequestError ? error
        : new PortalRequestError(controller.signal.aborted ? "timeout"
          : error instanceof SyntaxError ? "response" : "network", endpoint, responseStatus, requestId, phase);
      if (isRead && !retriedRead && (failure.kind === "network" || failure.kind === "timeout"
        || [408, 502, 503, 504].includes(failure.status || 0))) {
        retriedRead = true;
        continue;
      }
      throw failure;
    } finally {
      clearTimeout(timer);
    }
  }
}

/**
 * Uploads `body.file` after converting formats the worker would reject (HEIC,
 * AVIF, BMP, TIFF) to JPEG. Returns the file actually stored, so callers can
 * preview the converted image rather than an original the browser cannot show.
 */
export async function uploadPortalFile(body: FormData): Promise<{ key: string; file?: File }> {
  const picked = body.get("file");
  const prepared = picked instanceof File ? await prepareUploadFile(picked) : undefined;
  if (prepared && prepared.file !== picked) body.set("file", prepared.file, prepared.file.name);
  try {
    const key = await portalRequest("/api/upload", { method: "POST", body, timeoutMs: 120_000 }, async (response) => {
      const result = await response.json();
      if (typeof result?.key !== "string" || !result.key.startsWith("users/")) {
        throw new PortalRequestError("response", "/api/upload");
      }
      return result.key as string;
    });
    return { key, file: prepared?.file };
  } catch (error) {
    if (error instanceof PortalRequestError && prepared) error.fileInfo = prepared.info;
    throw error;
  }
}

export function fetchPortalBytes(path: string, authenticated = true): Promise<Uint8Array> {
  return portalRequest(path, { authenticated }, async (response) => new Uint8Array(await response.arrayBuffer()));
}
